/** Turkish formatting helpers — cari module. */

const qtyFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })
const amountFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Korgun currency code → symbol. P_DOV_Tip: EU=EURO, TL=Turkish Lira, US=Dollar. */
const CURRENCY_SYMBOL: Record<string, string> = {
  TL: '₺',
  EU: '€',
  US: '$',
}

export function currencySymbol(code: string | null): string {
  if (!code) return ''
  return CURRENCY_SYMBOL[code] ?? code
}

export function formatQty(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return qtyFormat.format(value)
}

export function formatAmount(value: number | null | undefined, code: string | null = null): string {
  if (value === null || value === undefined) return '—'
  const symbol = currencySymbol(code)
  return symbol ? `${amountFormat.format(value)} ${symbol}` : amountFormat.format(value)
}

/**
 * Money with no currency mark — for the borç/alacak columns of a statement
 * that is already scoped to one currency, where repeating the symbol on every
 * row is noise.
 */
export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return '—'
  return amountFormat.format(value)
}

/** API dates are 'YYYY-MM-DD' text; converted to dd.mm.yyyy without going through Date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [year, month, day] = iso.slice(0, 10).split('-')
  if (!year || !month || !day) return iso
  return `${day}.${month}.${year}`
}

/** 'YYYY-MM-DD HH:MM' → 'dd.mm.yyyy HH:MM'. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const [datePart, time] = value.split(' ')
  const d = formatDate(datePart ?? null)
  return time ? `${d} ${time}` : d
}

export function percentText(value: number): string {
  return `%${qtyFormat.format(value)}`
}

/**
 * Today by factory clock (Europe/Istanbul) — 'YYYY-MM-DD'.
 *
 * Reads the WORKSTATION's own clock and reprojects it onto Europe/Istanbul —
 * that fixes a browser in the wrong timezone (viewed from abroad, a laptop
 * left on UTC), but not a workstation whose system DATE is itself wrong; a
 * true factory-clock date would have to come from the server (`now() AT TIME
 * ZONE 'Europe/Istanbul'`, as the Worker uses elsewhere), which nothing this
 * screen already fetches carries. Same limitation as the identically-named
 * helper in `features/orders/format.ts`, an already-accepted pattern in this
 * codebase; used here only for the printed ekstre's non-critical "printed on"
 * footer stamp, not for any figure on the statement itself. The `en-CA`
 * locale conveniently already produces YYYY-MM-DD.
 */
const istanbulDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export function todayIstanbul(): string {
  return istanbulDayFormat.format(new Date())
}
